import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import theme from "@/theme";
import { ConversationTab } from "@/store/api/conversationsApiData";
import { useConversationsUnread } from "@/hooks/useConversationsUnread";

const tabs: { label: string; value: ConversationTab }[] = [
  { label: "All", value: "all" },
  { label: "Replies to my posts", value: "replies" },
  { label: "Mentions", value: "mentions" },
];

const ConversationsHeader: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as ConversationTab) || "all";

  const { count, frozenCount } = useConversationsUnread();

  const handleTabClick = (value: ConversationTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next);
  };

  const countFor = (value: ConversationTab) => {
    if (value === "all") return frozenCount ?? count;
    return undefined;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "54px",
        padding: "0 20px",
        backgroundColor: theme.palette.custom.mainWhite,
        borderBottom: "1px solid #F4F5F6",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 600,
          fontSize: "18px",
          lineHeight: "24px",
          color: theme.palette.custom.blueGray800,
          whiteSpace: "nowrap",
        }}
      >
        Conversations
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", height: "54px", pt: "10px" }}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.value;
          const count = countFor(tab.value);
          return (
            <Box
              key={tab.value}
              onClick={() => handleTabClick(tab.value)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                height: "100%",
                padding: "0 16px 2px",
                cursor: "pointer",
                borderBottom: `2px solid ${isActive ? theme.palette.custom.mainBlue : "transparent"}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  whiteSpace: "nowrap",
                  color: isActive ? theme.palette.custom.mainBlue : "#5a5f72",
                }}
              >
                {tab.label}
              </Typography>
              {tab.value === "all" && count != null && count > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "20px",
                    px: "6px",
                    borderRadius: "20px",
                    backgroundColor: "#dbeafe",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: "11px",
                      lineHeight: "16px",
                      color: "#1a56db",
                    }}
                  >
                    {count}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ConversationsHeader;
